import React, { useCallback } from 'react';
import { Tooltip } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { Results_NavLink_To, ManageAssets_NavLink_To, ManageProducts_NavLink_To } from '../../Constants/AppBar';
import { useDispatch, useSelector } from 'react-redux';
import * as searchActions from '../../redux/actions/searchActions';
import { matchSorter, rankings } from 'match-sorter';
import * as constants from '../../Constants/Search';
import CustomAutoComplete from '../common/CustomAutocomplete';
import { useState } from 'react';
import Typography from '@material-ui/core/Typography';
import { GlobalHotKeys } from 'react-hotkeys';
import CategorySelectionDropdown from './CategorySelectionDropdown';
import { debounce } from 'throttle-debounce';
import { apiCallDebounceTime } from '../../Constants/Api';
import { productJsonProps, productElasticSearchMatchFields } from '../../Constants/Products';
import CommonSearch from './CommonSearch';
import RouterBreadcrumbs from '../common/BreadCrumbs';
import { setResultsPageSearchTerm } from '../../redux/actions/resultsActions';
import { Grid, makeStyles } from '@material-ui/core/';
import { get } from 'lodash';
import { getProductImgUrl } from '../../Utils/ProductUtils';
import ProductWithThumbnail from '../Product/ProductWithThumbnail';
import { assetEsSearchFields } from '../../Constants/Asset';

const useStyles = makeStyles((theme) => ({
	versionLabel: { color: theme.palette.neutral.n400 },
	searchRoot: { height: 'calc(100vh - 88px)' },
	recenter: { marginTop: '-12rem' },
	small: {
		width: theme.spacing(7),
		height: theme.spacing(7),
		marginLeft: '1px',
	},
	productLabel: {
		marginLeft: '3px',
	},
}));

const searchLabel = 'Search Corgis';
const versionLabel = 'Product Catalog Edition';

export default function Search(props) {
	const classes = useStyles();
	const [isLoading, setLoading] = useState(false);
	const [openToolTip, setToolTipOpen] = React.useState(false);
	let onChangeCalled = false;

	const selectedCategory = useSelector((state) => state.search.category);
	const searchTerm = useSelector((state) => state.search.searchTerm);

	const handleToolTipClose = () => {
		setToolTipOpen(false);
	};

	const handleToolTipOpen = () => {
		setToolTipOpen(true);
	};
	//getting autocomplete options from store using useSelector hooks
	const assetOptions = useSelector((state) => state.search.options);

	const dispatch = useDispatch();

	let history = useHistory();

	//implementing debounce for search.
	//only trigger API call when there is a delay of mentioned time from last keystroke
	const DebounceLoadSearchAutoCompleteData = useCallback(
		debounce(apiCallDebounceTime, false, (searchQuery, selectedCategoryValue) =>
			LoadSearchAutoCompleteData(searchQuery, selectedCategoryValue),
		),
		[],
	);
	const LoadSearchAutoCompleteData = (searchQuery, selectedCategoryValue) => {
		setLoading(true);
		dispatch(searchActions.loadSearchData(searchQuery, selectedCategoryValue)).then(function () {
			setLoading(false);
		});
	};
	// default loading status is always false for the autocomplete
	// using match-sorter library to support multi field filtering for Material-UI autocomplete component as per docs (https://material-ui.com/components/autocomplete/#advanced).
	// at the moment autocomplete component only supports filtering with one field provided to prop "getOptionLabel"
	const filterOptions = (options, { inputValue }) => {
		//only trigger filtering when we have search term and options
		let keys = [];
		if (selectedCategory === constants.assets3dVal) {
			keys = assetEsSearchFields;
		}
		if (selectedCategory === constants.productVal) {
			keys = productElasticSearchMatchFields;
		}
		if (selectedCategory === constants.allVal) {
			keys = assetEsSearchFields.concat(productElasticSearchMatchFields);
		}
		if (options.length > 0 && inputValue.length > 0) {
			return matchSorter(options, inputValue, {
				//providing list of fields as filtering keys to enable multi-field filters.
				keys: keys,
				threshold: rankings.CONTAINS,
			});
		} else {
			return [];
		}
	};

	//This function handle the input change in the text field of the autocomplete component for every keypress and down events.
	const OnInputChange = (event, values) => {
		// Avoiding unnecessary calls to API when the search term is empty or just a space
		// to avoid call on selection of the option (during click event)
		// issue logged for material-UI community Prevent onInputChange when onChange is fired
		// https://github.com/mui-org/material-ui/issues/18784
		if (event.type === 'change') {
			dispatch(searchActions.setSearchTerm(values));
			dispatch(setResultsPageSearchTerm(values));
			if (values.length > 0 && values !== ' ' && values !== '') {
				DebounceLoadSearchAutoCompleteData(values, selectedCategory);
			}
		}
	};

	const returnAssetOptionName = (option) => {
		if (option.name) {
			return option.name;
		}
	};

	const returnProductOptionName = (option) => {
		// using lodash to get product name in nested object.
		const productName = get(option, productJsonProps.name);
		if (productName) {
			return productName;
		} else if (option.name) {
			// to load search query typed by user as a first result of autocomplete to take user to results page.
			return option.name;
		}
	};
	//returning option label name based on the type of the results.(assets, products, all)
	const onOptionsLabel = (option) => {
		if (typeof option === 'string') {
			return option;
		}
		if (option.type) {
			if (option.type === constants.productsGroupByType) {
				return returnProductOptionName(option);
			}
			if (option.type === constants.assets3dGroupByType) {
				return returnAssetOptionName(option);
			}
			if (option.type === constants.showResultsGroupByType) {
				return option.name;
			}
		}
	};
	const onValueChange = (event, value) => {
		onChangeCalled = true;
		event.stopPropagation();
		if (value !== null && typeof value === 'object') {
			// dispatching actions to fill in selectionOptions object to display on results view page
			dispatch(searchActions.setSelectionOption(value));
			// when clicked on the existing 3D assets will go to manage assets page
			if (value.type === constants.assets3dGroupByType) {
				history.push(ManageAssets_NavLink_To + '/' + value.id);
			}
			// when clicked on existing product will go to product details page.
			if (value.type === constants.productsGroupByType) {
				// TODO: remove this once id is available from index.
				history.push(ManageProducts_NavLink_To + '/' + get(value, productJsonProps.id, ''));
			}
		} else {
			return;
		}
	};
	// grouping by basing on the type of results (3d asset/product)
	const groupBy = (option) => {
		// only do a group by when `ALL` option is selected from the dropdown.
		if (selectedCategory === constants.allVal) {
			return option.type;
		}
		return;
	};
	//creating refs to set focus on the autocomplete when hot keys are pressed.
	const autocompleteSearch = React.useRef(null);
	const searchPageContainer = React.useRef(null);

	const setSearchAutoCompleteFocus = (event) => {
		autocompleteSearch.current.getElementsByTagName('input')[0].focus();
		event.preventDefault();
	};
	//creating Key and action mapping object
	const hotKeyMap = {
		FOCUS_SEARCH_AUTOCOMPLETE: 'shift+s',
	};
	const hotKeyHandlers = {
		FOCUS_SEARCH_AUTOCOMPLETE: setSearchAutoCompleteFocus,
	};
	//hook use effect which is equivalent to using component life cycles in a state full components
	React.useEffect(() => {
		//setting default focus to the search container div on component load.
		// because hot key mapping only works if the parent element is already on focus
		searchPageContainer.current.focus();
		// setting search autocomplete on focus after component mounts.
		autocompleteSearch.current.getElementsByTagName('input')[0].focus();
	}, []);
	const RenderOption = (option) => {
		// returning option based on the type of the results.(assets, products, all)
		if (typeof option === 'string') {
			return option;
		}
		if (option.type) {
			if (option.type === constants.productsGroupByType) {
				const productName = returnProductOptionName(option);
				const productId = get(option, productJsonProps.productId);
				return (
					<ProductWithThumbnail
						productName={`${productName} - ${productId}`}
						url={getProductImgUrl(option)}
					/>
				);
			}
			if (option.type === constants.assets3dGroupByType) {
				return (
					<React.Fragment>
						<span>{returnAssetOptionName(option)}</span>
					</React.Fragment>
				);
			}
			if (option.type === constants.showResultsGroupByType) {
				return (
					<React.Fragment>
						<span>{option.name}</span>
					</React.Fragment>
				);
			}
		}
	};
	const handleKeyDown = (e) => {
		// navigate to results page when enter button is pressed.only when onChange is not called
		if (e.keyCode === 13 && !onChangeCalled) {
			history.push(Results_NavLink_To + '/' + searchTerm);
		}
	};
	//props for custom autocomplete (reusable search component)
	const customAutoCompleteProps = {
		onInputChange: OnInputChange,
		onValueChange: onValueChange,
		filterOptions: filterOptions,
		getOptionsLabel: onOptionsLabel,
		options: assetOptions,
		placeHolder: 'Search...',
		size: 'small',
		groupBy: groupBy,
		displayStartAdornment: true,
		loading: isLoading,
		renderOption: RenderOption,
		id: 'custom3DAssetsAndProductsSearch',
		onKeyDown: handleKeyDown,
	};
	return (
		<Grid container item className={classes.searchRoot} justify="flex-start">
			<RouterBreadcrumbs />
			<Grid container item direction="column" ref={searchPageContainer} className={classes.recenter}>
				<GlobalHotKeys keyMap={hotKeyMap} handlers={hotKeyHandlers} />
				<Grid container item direction="column" alignItems="center" justify="center" spacing={4}>
					{/* typography */}
					<Grid container item direction="column" alignItems="center" spacing={1}>
						<Grid item>
							<Typography variant="h1">{searchLabel}</Typography>
						</Grid>
						<Grid item>
							<Typography className={classes.versionLabel} variant="caption">
								{versionLabel}
							</Typography>
						</Grid>
					</Grid>
					{/* input fields */}
					<Grid container style={{ width: '55em' }}>
						<Grid item xs={12} sm={4} md={3} lg={3}>
							<CategorySelectionDropdown
								{...props}
								DebounceLoadSearchAutoCompleteData={DebounceLoadSearchAutoCompleteData}
							/>
						</Grid>
						<Grid item xs={12} sm={8} md={9} lg={9}>
							{/* wrapping tool tip around search auto complete */}
							<Tooltip
								open={openToolTip}
								onClose={handleToolTipClose}
								onOpen={handleToolTipOpen}
								arrow={true}
								placement="top"
								enterDelay={500}
								enterNextDelay={500}
								id="homeSearchAutocompleteToolTip"
								disableFocusListener
								disableTouchListener
								title={
									<React.Fragment>
										<Typography color="inherit">Open Quick Search</Typography>
										<b>{'To Start a search from anywhere you can'}</b>.<br></br>
										<b>{'also open the search with the hotkey:'}</b>.<br></br>
										<b>{'\u2191  SHIFT +  s'}</b>
									</React.Fragment>
								}
							>
								<div
									onClick={() => {
										setToolTipOpen(false);
									}}
								>
									<CustomAutoComplete {...customAutoCompleteProps} ref={autocompleteSearch} />
								</div>
							</Tooltip>
							<CommonSearch />
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
}
